import React, { useRef, useState } from "react";
import { Camera, Upload, X, Image as ImageIcon } from "lucide-react";
import UploadProgressPie from "./UploadProgressPie";

/*
 * ImageUpload — file/camera picker that compresses the chosen image to a
 * JPEG data URI on the client and emits it via onChange. The data URI can
 * be stored directly in MongoDB string fields (dish image_url, restaurant
 * logo_url / cover_url) without needing a separate object-storage layer.
 */
export default function ImageUpload({
  label,
  value,
  onChange,
  testId = "image-upload",
  maxWidth = 1280,
  quality = 0.78,
  aspect = "video", // "video" 16/9, "square" 1/1, "wide" 21/9
}) {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(""); // "read" | "process"
  const [err, setErr] = useState(null);

  const aspectClass = aspect === "square" ? "aspect-square" : aspect === "wide" ? "aspect-[21/9]" : "aspect-video";

  const handleFile = (file) => {
    setErr(null);
    if (!file) return;
    if (!file.type.startsWith("image/")) { setErr("File non valido (solo immagini)"); return; }
    if (file.size > 10 * 1024 * 1024) { setErr("Immagine troppo grande (max 10MB)"); return; }
    setBusy(true);
    setProgress(0);
    setPhase("read");
    const reader = new FileReader();
    reader.onprogress = (ev) => {
      if (ev.lengthComputable) {
        // reading is roughly the first 70% of perceived progress
        const p = Math.min(70, Math.round((ev.loaded / ev.total) * 70));
        setProgress(p);
      }
    };
    reader.onerror = () => { setErr("Errore lettura file"); setBusy(false); setProgress(0); setPhase(""); };
    reader.onload = (ev) => {
      setPhase("process");
      setProgress(75);
      const img = new Image();
      img.onerror = () => { setErr("Immagine non leggibile"); setBusy(false); setProgress(0); setPhase(""); };
      img.onload = () => {
        try {
          setProgress(85);
          const canvas = document.createElement("canvas");
          let w = img.width, h = img.height;
          if (w > maxWidth) {
            h = Math.round(h * (maxWidth / w));
            w = maxWidth;
          }
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          setProgress(95);
          const dataUri = canvas.toDataURL("image/jpeg", quality);
          onChange(dataUri);
          setProgress(100);
        } catch (e) {
          setErr("Errore compressione immagine");
        } finally {
          // small delay so the user sees 100% before the pie disappears
          setTimeout(() => { setBusy(false); setProgress(0); setPhase(""); }, 350);
        }
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const clear = () => { onChange(""); if (fileRef.current) fileRef.current.value = ""; if (camRef.current) camRef.current.value = ""; };

  return (
    <div data-testid={testId}>
      {label && <label className="input-label">{label}</label>}

      <div className={`relative ${aspectClass} rounded-xl border border-dashed border-line bg-deep-card/60 overflow-hidden`}>
        {value ? (
          <>
            <img src={value} alt="preview" className="absolute inset-0 w-full h-full object-cover" />
            <button
              type="button"
              onClick={clear}
              className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white w-8 h-8 rounded-full flex items-center justify-center transition"
              data-testid={`${testId}-clear`}
              aria-label="Rimuovi immagine"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500 text-xs">
            {busy ? (
              <UploadProgressPie
                percent={progress}
                size={64}
                label={phase === "read" ? "Caricamento" : phase === "process" ? "Compressione" : ""}
                testId={`${testId}-pie`}
              />
            ) : (
              <>
                <ImageIcon className="w-7 h-7 mb-2 text-zinc-600" />
                <span className="font-mono uppercase tracking-widest text-[10px]">No image</span>
              </>
            )}
          </div>
        )}
      </div>

      {busy && value && (
        <div className="mt-2 flex items-center justify-center" data-testid={`${testId}-pie-overlay`}>
          <UploadProgressPie percent={progress} size={48} testId={`${testId}-pie-mini`} />
        </div>
      )}

      <div className="mt-2 flex gap-2 flex-wrap">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
          data-testid={`${testId}-file-input`}
        />
        <input
          ref={camRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
          data-testid={`${testId}-camera-input`}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="btn-outline !py-1.5 text-xs"
          data-testid={`${testId}-pick`}
        >
          <Upload className="w-3.5 h-3.5" /> Carica
        </button>
        <button
          type="button"
          onClick={() => camRef.current?.click()}
          disabled={busy}
          className="btn-outline !py-1.5 text-xs"
          data-testid={`${testId}-camera`}
        >
          <Camera className="w-3.5 h-3.5" /> Foto
        </button>
        {value && (
          <button
            type="button"
            onClick={clear}
            disabled={busy}
            className="btn-ghost text-xs text-red-400 hover:text-red-300"
            data-testid={`${testId}-remove`}
          >
            Rimuovi
          </button>
        )}
      </div>

      {err && <p className="text-[10px] text-red-400 mt-1.5" data-testid={`${testId}-error`}>{err}</p>}
    </div>
  );
}
